import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { AudienceTags } from './AudienceTags';
import { Mail, CheckCircle2 } from 'lucide-react';

export function HeroSection() {
  const { t } = useTranslation('home');
  const { t: tCommon } = useTranslation();
  const { locale } = useParams<{ locale: string }>();
  const substackUrl = 'https://corentinleroux.substack.com';
  const [subscribed, setSubscribed] = useState(false);

  const handleNewsletterSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;

    // Envoyer à Substack en arrière-plan (no-cors car domaine différent)
    fetch(`${substackUrl}/api/v1/free`, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ email }),
    });

    setSubscribed(true);
  };

  return (
    <section className="bg-aspexit-gray-light pt-6 md:pt-8 lg:pt-10 pb-12 md:pb-16 lg:pb-20">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Tags d'audience */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <AudienceTags />
        </motion.div>

        {/* Titre principal — style stitch-v1 */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold tracking-tight leading-[1.1] mt-8 mb-6"
        >
          <span className="text-aspexit-black">{t('hero.titleLine1')}{' '}</span>
          <br />
          <span className="text-aspexit-green">{t('hero.titleHighlight')}</span>
        </motion.h1>

        {/* Sous-titre */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-lg md:text-xl text-aspexit-gray mb-10 leading-relaxed max-w-2xl"
        >
          {t('hero.subtitle')}
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Button
            size="xl"
            variant="default"
            className="group"
            onClick={() => document.getElementById('services-section')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <span className="flex items-center justify-center gap-2">
              {t('hero.cta')}
              <svg className="w-5 h-5 group-hover:translate-y-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </span>
          </Button>
          <Button asChild size="xl" variant="outline">
            <Link to={`/${locale}/blog`}>{t('hero.ctaSecondary')}</Link>
          </Button>
        </motion.div>

        {/* Newsletter compacte — style stitch-v1 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 bg-white border border-gray-200 rounded-xl p-6 md:p-8 flex flex-col md:flex-row justify-between items-center gap-6 shadow-sm"
        >
          <div className="flex items-center gap-3 shrink-0">
            <div className="w-10 h-10 rounded-full bg-aspexit-green/10 flex items-center justify-center">
              {subscribed ? (
                <CheckCircle2 className="w-5 h-5 text-aspexit-green" />
              ) : (
                <Mail className="w-5 h-5 text-aspexit-green" />
              )}
            </div>
            <div>
              <h3 className="font-heading font-bold text-aspexit-black text-base">
                {subscribed ? tCommon('newsletter.success') : tCommon('newsletter.joinTitle')}
              </h3>
              <p className="text-sm text-aspexit-gray hidden sm:block">
                {subscribed ? tCommon('newsletter.poweredBy') : tCommon('newsletter.description')}
              </p>
            </div>
          </div>
          {!subscribed && (
            <form
              onSubmit={handleNewsletterSubmit}
              className="w-full md:w-auto flex gap-0 border-b-2 border-gray-300 focus-within:border-aspexit-green transition-colors"
            >
              <input
                type="email"
                name="email"
                placeholder={tCommon('newsletter.emailPlaceholder')}
                required
                className="bg-transparent border-none focus:ring-0 text-aspexit-black py-3 px-0 w-full md:w-64 placeholder:text-gray-400 text-sm"
              />
              <button
                type="submit"
                className="text-aspexit-green font-bold uppercase tracking-widest text-xs px-4 py-3 hover:text-aspexit-green-dark transition-colors whitespace-nowrap"
              >
                {tCommon('newsletter.subscribe')}
              </button>
            </form>
          )}
        </motion.div>

      </div>
    </section>
  );
}
